import React, { useContext, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import * as Sentry from "@sentry/react";

// Import de la configuration Sentry
import initSentry, { setSentryUser } from './initSentry';

import { AuthProvider, AuthContext } from './context/AuthContext';
import PrivateRoute from './route/PrivateRoute';
import PublicRoute from './route/PublicRoute';

// Importation de toutes les pages
import HomePage from './pages/HomePage';
import TransferPage from './pages/TransferPage';
import InitTranferPage from './pages/InitTranferPage';
import SupportPage from './pages/SupportPage';
import ProfilePage from './pages/ProfilePage';
import UsersPage from './pages/UsersPage';
//import AccountDeletionRequestsPage from './pages/AccountDeletionRequestsPage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import IdentificationPage from './pages/IdentificationPage';
import NotFoundPage from './pages/NotFoundPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ResetPasswordPage1 from './pages/ResetPasswordPage1';
import AccountDeletionRequestPage from './pages/AccountDeletionRequestPage';
import ResetPasswordPage2 from './pages/ResetPasswordPage2';
import RetrieveUsernamePage from './pages/RetrieveUsernamePage';

import BankSettings from './components/settings/BankSettings';
import WalletSettings from './components/settings/WalletSettings';
import GeneralSettings from './components/settings/GeneralSettings';
import RecipientPage from './pages/RecipientPage';

// Importation des styles globaux si nécessaire
import './assets/css/style.css';
import './assets/css/media-query.css';
import IndexPage from "./pages/IndexPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import AccountDeletionRequestsPage from "./pages/AccountDeletionRequestsPage";

// Initialiser Sentry au démarrage
initSentry();

// Wrapper pour intégrer la surveillance Sentry avec l'authentification
const SentryUserProvider = ({ children }) => {
    const { user } = useContext(AuthContext);

    useEffect(() => {
        if (user) {
            // Définir l'utilisateur dans Sentry quand il se connecte
            setSentryUser(user);
        } else {
            // Effacer l'utilisateur de Sentry quand il se déconnecte
            Sentry.setUser(null);
        }
    }, [user]);

    return children;
};

// Composant de redirection conditionnelle pour les utilisateurs connectés
const RedirectIfAuthenticated = ({ children }) => {
    const { user } = useContext(AuthContext);

    // Si l'utilisateur est connecté, redirige vers la page d'accueil
    if (user) {
        return <Navigate to="/home" replace />;
    }

    return children;
};

// Wrapper Sentry pour les routes - capture automatiquement les erreurs de routing
const SentryRoutes = Sentry.withSentryRouting(Routes);

function App() {
    return (
        <AuthProvider>
            <Router>
                <SentryUserProvider>
                    <SentryRoutes>
                        {/* Routes publiques */}
                        <Route
                            path="/login"
                            element={
                                <RedirectIfAuthenticated>
                                    <PublicRoute>
                                        <LoginPage />
                                    </PublicRoute>
                                </RedirectIfAuthenticated>
                            }
                        />
                        <Route
                            path="/register"
                            element={
                                <RedirectIfAuthenticated>
                                    <PublicRoute>
                                        <RegisterPage />
                                    </PublicRoute>
                                </RedirectIfAuthenticated>
                            }
                        />
                        <Route
                            path="/"
                            element={
                                <RedirectIfAuthenticated>
                                    <PublicRoute>
                                        <IndexPage />
                                    </PublicRoute>
                                </RedirectIfAuthenticated>
                            }
                        />
                        <Route
                            path="/privacy_policy"
                            element={
                                <RedirectIfAuthenticated>
                                    <PublicRoute>
                                        <PrivacyPolicyPage />
                                    </PublicRoute>
                                </RedirectIfAuthenticated>
                            }
                        />

                        <Route
                            path="/account-deletion-request"
                            element={
                                <RedirectIfAuthenticated>
                                    <PublicRoute>
                                        <AccountDeletionRequestPage />
                                    </PublicRoute>
                                </RedirectIfAuthenticated>
                            }
                        />
                        <Route
                            path="/reset-password1"
                            element={
                                <PublicRoute>
                                    <ResetPasswordPage1 />
                                </PublicRoute>
                            }
                        />
                        <Route
                            path="/reset-password2"
                            element={
                                <PublicRoute>
                                    <ResetPasswordPage2 />
                                </PublicRoute>
                            }
                        />

                        <Route
                            path="/retrieve-username"
                            element={
                                <PublicRoute>
                                    <RetrieveUsernamePage />
                                </PublicRoute>
                            }
                        />

                        {/* Routes privées */}
                        <Route
                            path="/home"
                            element={
                                <PrivateRoute>
                                    <HomePage />
                                </PrivateRoute>
                            }
                        />

                        <Route
                            path="/reset-password"
                            element={
                                <PrivateRoute>
                                    <ResetPasswordPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/identity"
                            element={
                                <PrivateRoute>
                                    <IdentificationPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/init_transfer"
                            element={
                                <PrivateRoute>
                                    <InitTranferPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/transfer"
                            element={
                                <PrivateRoute>
                                    <TransferPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/support"
                            element={
                                <PrivateRoute>
                                    <SupportPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/profile"
                            element={
                                <PrivateRoute>
                                    <ProfilePage />
                                </PrivateRoute>
                            }
                        />

                        {/* Routes réservées à l'administrateur */}
                        <Route
                            path="/users"
                            element={
                                <PrivateRoute adminOnly>
                                    <UsersPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/account-deletion-requests"
                            element={
                                <PrivateRoute adminOnly>
                                    <AccountDeletionRequestsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/settings"
                            element={
                                <PrivateRoute adminOnly>
                                    <SettingsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/banks"
                            element={
                                <PrivateRoute adminOnly>
                                    <BankSettings />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/wallets"
                            element={
                                <PrivateRoute adminOnly>
                                    <WalletSettings />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/general-settings"
                            element={
                                <PrivateRoute adminOnly>
                                    <GeneralSettings />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/recipient"
                            element={
                                <PrivateRoute>
                                    <RecipientPage />
                                </PrivateRoute>
                            }
                        />

                        {/* Route pour la page 404 */}
                        <Route path="/404" element={<NotFoundPage />} />

                        {/* Route générique for les pages non trouvées */}
                        <Route path="*" element={<NotFoundPage />} />
                    </SentryRoutes>
                </SentryUserProvider>
            </Router>
        </AuthProvider>
    );
}

// Exporter l'App wrappée avec Sentry pour capturer les erreurs globales
export default Sentry.withProfiler(Sentry.withErrorBoundary(App, {
    fallback: ({ error, resetError }) => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            textAlign: 'center',
            padding: '20px'
        }}>
            <h1>Une erreur inattendue s'est produite</h1>
            <p>Nous avons été notifiés de ce problème et travaillons à le résoudre.</p>
            <details style={{ whiteSpace: 'pre-wrap', marginTop: '20px' }}>
                {error && error.toString()}
            </details>
            <button
                onClick={resetError}
                style={{
                    marginTop: '20px',
                    padding: '10px 20px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                }}
            >
                Réessayer
            </button>
        </div>
    ),
    showDialog: true,
}));